import type { BayesianProbe } from '../types/engagement'
import type { CapabilityRow, NextAction, PilotFact, Principle } from '../types/snak3'

export const snak3Images = {
  retinaAI: {
    src: 'https://cdn.magicpatterns.com/patterns/generated-images/de64cca4-0905-4336-b148-668df4c6646b.jpg',
    alt: 'Colour retinal fundus image with an analytical overlay tracing vessels and marker points',
  },
  clinic: {
    src: 'https://cdn.magicpatterns.com/patterns/generated-images/959089a3-4422-4599-ab12-14ee5799271f.jpg',
    alt: 'Retina specialist reviewing OCT scans on a monitor in a clinic',
  },
  predict: {
    src: 'https://cdn.magicpatterns.com/patterns/generated-images/a44934df-5734-4d1c-ad8c-f0c6cc2c86ac.jpg',
    alt: 'Abstract close-up of an iris with luminous data points and concentric measurement rings',
  },
}

export const bayesProbe: BayesianProbe = {
  trigger: 'Your prior?',
  priorLabel: 'Step 1 · state your prior',
  priorQuestion:
    'Before the data: how closely do you think two experienced readers agree when counting hyperreflective foci on the same scan?',
  priorOptions: [
    { id: 'high', label: 'Closely — it is a visual count' },
    { id: 'mid', label: 'Broadly, with some spread' },
    { id: 'low', label: 'Poorly — nobody counts them the same way' },
  ],
  evidenceLabel: 'Step 2 · the evidence',
  evidenceStat: 'Same scan, different counts',
  evidenceText:
    'Manual assessment of small hyperreflective foci is well recognised as variable between readers and between sessions. Automated segmentation does not read better than you — it reads identically every time, which is what makes a change between visits interpretable.',
  posteriorLabel: 'Step 3 · update',
  posteriorQuestion: 'Would a consistent automated count change what you record?',
  posteriorOptions: [
    { id: 'no', label: 'No — my read is enough', share: 14 },
    { id: 'follow', label: 'Possibly, for follow-up comparison', share: 49 },
    { id: 'all', label: 'Yes, for every DME scan', share: 37 },
  ],
  closing:
    'Prior, evidence, update — the same loop a pilot runs. Measure your own clinic before deciding what to believe about it.',
  reference:
    'Peer shares are illustrative engagement data. Reader variability reflects published discussion of manual OCT biomarker assessment.',
}

export const snak3Rail = {
  chapter: 'Snak 3',
  eyebrow: 'Advanced · ignite precision · fully branded',
  title: ['Pilot the', 'prediction.'],
  blurb:
    'For clinics already practising precision medicine. Snak 3 is not persuasion — it offers infrastructure: AI in routine practice, and an openly aspirational look at predicting response.',
}

/* ── Screen 1 · Principles ─────────────────────────────────────────── */

export const principles = {
  eyebrow: 'Advanced · ignite precision',
  headline: ['You already practise', 'precision medicine.'],
  lead: 'Not as a programme, but as a habit. The gap is not conviction — it is the infrastructure to do it consistently and to measure whether it worked.',
  takeaway: 'What is missing is not belief. It is instrumentation.',
  cta: 'What AI can and cannot do',
}

export const precisionPrinciples: Principle[] = [
  { label: 'Right patient', already: 'You already treat the refractory eye differently.' },
  { label: 'Right intervention', already: 'You already weigh the driver, not just the thickness.' },
  { label: 'Right time', already: 'You already know late looks different from early.' },
  { label: 'Measured', already: 'This is the part that is usually left to impression.' },
]

/* ── Screen 2 · AI fit-to-clinic ───────────────────────────────────── */

export const fitToClinic = {
  headline: ['AI fit-to-clinic.', 'Honestly.'],
  lead: 'The useful version of this conversation is about scope. Segmentation is a measurement tool, not a clinician.',
  doesLabel: 'What it does',
  doesNotLabel: 'What it does not do',
  takeaway: 'A second reader that never tires — and never carries the responsibility.',
  cta: 'Run it in your own clinic',
}

export const capabilities: CapabilityRow[] = [
  { does: 'Segments the five biomarkers the same way every time', doesNot: 'Make a diagnosis' },
  { does: 'Counts foci no one counts by hand', doesNot: 'Choose a therapy' },
  { does: 'Compares this visit to the last one on identical rules', doesNot: 'Replace your read' },
  { does: 'Flags a change of cluster between visits', doesNot: 'Validate itself in your population' },
]

/* ── Screen 3 · The pilot ──────────────────────────────────────────── */

export const pilot = {
  headline: 'A pilot you could actually run.',
  lead: 'Retrospective, anonymised, and small enough to finish. The point is to see what your own clinic looks like rather than what a study population looked like.',
  takeaway: 'The question becomes empirical: what is the cluster distribution in your clinic?',
  cta: 'The aspirational part',
}

export const pilotFacts: PilotFact[] = [
  { label: 'Scope', value: '50 consecutive DME scans, retrospective' },
  { label: 'Duration', value: '8 weeks, no additional patient visits' },
  { label: 'We bring', value: 'Segmentation, analysis support, methodology' },
  { label: 'You get', value: 'Your own cluster distribution and inter-reader comparison' },
  { label: 'Governance', value: 'Local data agreement, fully anonymised' },
]

/* ── Screen 4 · Differentiation tail · Predict ─────────────────────── */

export const predict = {
  label: 'Differentiation · Advanced',
  status: 'Aspirational · hypothesis-generating',
  headline: ['Predict, not just', 'characterise.'],
  hypothesis:
    'If phenotype tracks inflammatory burden, could phenotype eventually anticipate which patients respond to which arm of therapy?',
  lead: 'This is the open question, stated as an open question. It is where precision phenotyping would become genuinely predictive rather than descriptive.',
  chips: ['Not established', 'No predictive claim', 'Research question'],
  productName: 'Ozurdex®',
  productDescriptor: 'dexamethasone intravitreal implant 0.7 mg',
  productLine:
    'A sustained-release intravitreal corticosteroid, indicated for adult patients with visual impairment due to diabetic macular oedema.',
  productFrame:
    'Any link between phenotype and response to a given therapy — including Ozurdex® — is a hypothesis for investigation, not a claim.',
  spc: 'Consult the Summary of Product Characteristics before prescribing. Report adverse events in line with national requirements.',
  internalNote:
    'Internal: aspirational positioning per the adoption model. No efficacy or predictive claim may be made until data and approval exist.',
  cta: 'Where you come in',
}

/* ── Screen 5 · Co-create ──────────────────────────────────────────── */

export const coCreate = {
  eyebrow: 'Where this ends',
  headline: ['Ignite it with', 'your own data.'],
  copy: 'Advanced clinics do not need another message. They need a project, a dataset and peers to compare it against.',
  arc: [
    'Snak 1 — DME is not one pattern',
    'Snak 2 — the window closes quietly',
    'Snak 3 — pilot it, then predict it',
  ],
  closing: 'Precision medicine adoption is not a message. It is a practice.',
}

export const nextActions: NextAction[] = [
  {
    label: 'Join the phenotyping pilot',
    detail: '50 scans, 8 weeks, your own distribution',
    primary: true,
  },
  { label: 'Contribute to the Danish dataset', detail: 'Anonymised scans, local data agreement' },
  { label: 'Join the peer faculty', detail: 'Quarterly case forum with Danish retina specialists' },
]

/* ── Shared ────────────────────────────────────────────────────────── */

export const snak3References = {
  principles: 'Precision-medicine framing after the AbbVie adoption model: Foundational, Developing, Advanced.',
  fitToClinic:
    'Capability scope reflects AI-assisted OCT segmentation as a measurement aid. Clinical interpretation remains with the clinician.',
  pilot: 'Pilot outline for discussion. Subject to local data-governance approval.',
  predict:
    'Aspirational research direction. No predictive claim is made for any therapy. Ozurdex® prescribing information in the Summary of Product Characteristics.',
  coCreate: 'Branded instalment. Intended for healthcare professionals in Denmark.',
}

export const snak3Disclaimer =
  'Phase 3 · Advanced archetype. Fully branded. Predictive biomarker content is aspirational and hypothesis-generating: no predictive claim is made for any therapy. Morphological phenotype clusters do not determine treatment. Consult the Summary of Product Characteristics before prescribing and report adverse events in line with national requirements.'
